import React, { useState } from "react";
import { Button, Column, Icon, Row, Sheet, Text } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";

type ModeSelectionSheetProps = {
  open: boolean;
  onClose: () => void;
  onSelectMode: (mode: "standard" | "stream" | "stream-dev") => void;
  isEngineer: boolean;
};

const ModeSelectionSheet = ({
  open,
  onClose,
  onSelectMode,
  isEngineer,
}: ModeSelectionSheetProps) => {
  const [selectedMode, setSelectedMode] = useState<"standard" | "stream" | "stream-dev" | null>(null);

  const handleConfirm = () => {
    if (selectedMode) {
      onSelectMode(selectedMode);
    }
  };

  return (
    <Sheet
      className="mode-selection-sheet"
      type="overlay"
      side="bottom"
      open={open}
      onClose={onClose}
    >
      <div style={{ borderRadius: "21px" }}>
        <Column spacing="400">
          <Row alignmentHorizontal="justify">
            <Text type="h400">Select Inspection Mode</Text>
            <Button onClick={onClose} type="icon">
              <Icon tokens={closeSmallTokens} />
            </Button>
          </Row>

          <Column spacing="300">
            <Text type="b200">
              Choose how you want to capture and analyze inspection evidence:
            </Text>

            {/* Standard Mode Option */}
            <div
              style={{
                border: selectedMode === "standard" ? "2px solid #0073BB" : "1px solid #D5D5D5",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: selectedMode === "standard" ? "#F0F8FF" : "white",
                padding: "16px",
              }}
              onClick={() => setSelectedMode("standard")}
            >
              <Row alignmentVertical="center" spacing="300">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: selectedMode === "standard" ? "6px solid #0073BB" : "2px solid #D5D5D5",
                    backgroundColor: "white",
                    flexShrink: 0,
                  }}
                />
                <Column spacing="100">
                  <Text type="b300">Standard Mode</Text>
                  <Text type="b100" color="secondary">
                    Manually capture images when ready. Full control over when to take photos.
                  </Text>
                </Column>
              </Row>
            </div>

            {/* Stream Mode Option */}
            <div
              style={{
                border: selectedMode === "stream" ? "2px solid #0073BB" : "1px solid #D5D5D5",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: selectedMode === "stream" ? "#F0F8FF" : "white",
                padding: "16px",
              }}
              onClick={() => setSelectedMode("stream")}
            >
              <Row alignmentVertical="center" spacing="300">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    border: selectedMode === "stream" ? "6px solid #0073BB" : "2px solid #D5D5D5",
                    backgroundColor: "white",
                    flexShrink: 0,
                  }}
                />
                <Column spacing="100">
                  <Text type="b300">Stream Mode (AI-Assisted)</Text>
                  <Text type="b100" color="secondary">
                    Real-time AI guidance with automatic detection. AI helps identify the perfect moment to capture.
                  </Text>
                </Column>
              </Row>
            </div>

            {/* Stream Dev Mode Option - Only for Engineers */}
            {isEngineer && (
              <div
                style={{
                  border: selectedMode === "stream-dev" ? "2px solid #0073BB" : "1px solid #D5D5D5",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: selectedMode === "stream-dev" ? "#F0F8FF" : "white",
                  padding: "16px",
                }}
                onClick={() => setSelectedMode("stream-dev")}
              >
                <Row alignmentVertical="center" spacing="300">
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: selectedMode === "stream-dev" ? "6px solid #0073BB" : "2px solid #D5D5D5",
                      backgroundColor: "white",
                      flexShrink: 0,
                    }}
                  />
                  <Column spacing="100">
                    <Text type="b300">Stream Dev Mode</Text>
                    <Text type="b100" color="secondary">
                      Advanced streaming with configurable quality settings. For engineers to test and optimize performance.
                    </Text>
                  </Column>
                </Row>
              </div>
            )}
          </Column>

          <Row
            alignmentHorizontal="justify"
            widths="fill"
            spacingInset="400 none none"
          >
            <Button onClick={onClose} type="tertiary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              type="primary"
              disabled={!selectedMode}
            >
              Start Inspection
            </Button>
          </Row>
        </Column>
      </div>
    </Sheet>
  );
};

export default ModeSelectionSheet;
